import { _ } from "../../../polyglot/polyglot.js";
import {
	angleLeftSolidIcon,
	angleRightSolidIcon,
} from "../../../elements/icons/index.js";
import { range } from "@foretagsplatsen/core-helpers/array";
import { Widget } from "@foretagsplatsen/widgetjs";

const numberOfButtonsToShow = 9;

/**
 * Render a list of buttons for navigation. At max, 9 buttons are displayed.
 * If more pages are available, the first/last button turn into a "..." navigation button.
 *
 * @param {function():number} getPageIndex - Function invoked to retrieve the current page index
 * @param {function():number} getPageSize - Function invoked to retrieve the number of items per page
 * @param {function():number} getTotalNumberOfItems - Function invoked to retrieve the total number of items
 * @param {function(number): void} onNavigationClick - Function invoked when a button is clicked.
 *   The function is invoked with the page index of the button
 * @param {string} [cssClass] - Class for the container
 */
export default class PaginationButtonList extends Widget {
	constructor({
		getPageIndex,
		getPageSize,
		getTotalNumberOfItems,
		onNavigationClick,
		cssClass = "",
	}) {
		super(...arguments);

		this._getPageIndex = getPageIndex;
		this._getPageSize = getPageSize;
		this._getTotalNumberOfItems = getTotalNumberOfItems;
		this._onNavigationClick = onNavigationClick;
		this._cssClass = cssClass;

		// This widget allows to "translate" the visible
		// navigation buttons using "..." buttons.
		// We use the following variable to keep track of the
		// offset of the navigation buttons.
		//
		// Example:
		//   - default view:          ... 3  4  5  6  7 ...
		//   - with an offset of -2:   1  2  3  4  5 ...
		//   - with an offset of 2:    5  6  7  8  9
		//
		this._navigationButtonOffset = 0;

		// We cache the current page index, so that
		// on content update, we can internally reset the
		// offset if needed.
		this._currentPageIndex = null;

		// We cache the total number of items, so that on content
		// update, if getTotalNumberOfItems() returns null or
		// undefined, the widget can decide to either hide itself or
		// show disabled buttons corresponding to the previous number
		// of items.
		this._totalNumberOfItems = undefined;
	}

	renderContentOn(html) {
		const totalNumberOfItems = this._getTotalNumberOfItems();
		let enabled;

		if (totalNumberOfItems) {
			this._totalNumberOfItems = totalNumberOfItems;
			enabled = true;
		} else {
			if (!this._totalNumberOfItems) return;

			enabled = false;
		}

		const currentPageIndex = this._getPageIndex();
		if (this._currentPageIndex !== currentPageIndex) {
			this._navigationButtonOffset = 0;
			this._currentPageIndex = currentPageIndex;
		}

		const numberOfPages = Math.ceil(
			this._totalNumberOfItems / this._getPageSize(),
		);

		if (numberOfPages === 1) return;

		html.div(
			{
				class: ["pagination-button-list", this._cssClass],
				"data-test": "pagination-button-list",
			},
			html.div(
				{ class: "navigation-buttons" },
				this._previousButtonRenderer({ currentPageIndex, enabled }),
				this._navigationButtonsRenderer({
					currentPageIndex,
					numberOfPages,
					enabled,
				}),
				this._nextButtonRenderer({
					currentPageIndex,
					numberOfPages,
					enabled,
				}),
			),
			html.div(
				{ class: "total", "data-test": "total" },
				_("{totalNumberOfItems} items", {
					totalNumberOfItems: this._totalNumberOfItems,
				}),
			),
		);
	}

	_previousButtonRenderer({ currentPageIndex, enabled }) {
		const disabled = currentPageIndex === 0 || !enabled;
		return (html) =>
			html.div(
				{
					class: [
						"navigation-button",
						"previous-button",
						{ disabled },
					],
					"data-test": "previous-button",
					click: () =>
						!disabled &&
						this._triggerOnClick({
							pageIndex: currentPageIndex - 1,
						}),
				},
				angleLeftSolidIcon,
			);
	}

	_nextButtonRenderer({ currentPageIndex, numberOfPages, enabled }) {
		const disabled = currentPageIndex === numberOfPages - 1 || !enabled;
		return (html) =>
			html.div(
				{
					class: [
						"navigation-button",
						"previous-button",
						{ disabled },
					],
					"data-test": "next-button",
					click: () =>
						!disabled &&
						this._triggerOnClick({
							pageIndex: currentPageIndex + 1,
						}),
				},
				angleRightSolidIcon,
			);
	}

	_navigationButtonsRenderer({ currentPageIndex, numberOfPages, enabled }) {
		const buttonRange = this._getButtonsRange({
			currentPageIndex,
			numberOfPages,
		});

		return buttonRange.map((buttonRangeIndex, index) =>
			this._buttonRenderer({
				pageIndex: buttonRangeIndex + this._navigationButtonOffset,
				index,
				numberOfPages,
				enabled,
			}),
		);
	}

	/**
	 * Return the range of buttons to render, around the provided `currentPageIndex`.
	 * The goal is to have `numberOfButtonsToShow` most of the time, and to "center"
	 * `pageIndex` within the range if possible.
	 *
	 * @example
	 * currentPageIndex=1, numberOfPages=1000 -> [0 1 2 3 4 5 6 7 8]
	 * currentPageIndex=10, numberOfPages=1000 -> [6 7 8 9 10 11 12 13 14]
	 * currentPageIndex=10, numberOfPages=12 -> [4 5 6 7 8 9 10 11 12]
	 * @param {int} currentPageIndex - Current page index
	 * @param {int} numberOfPages - Total number of pages, ie upper limit for the range
	 * @return {int[]}
	 */
	_getButtonsRange({ currentPageIndex, numberOfPages }) {
		const middleIndex = (numberOfButtonsToShow - 1) / 2;

		const lastPageIndex = Math.min(
			currentPageIndex + middleIndex,
			numberOfPages,
		);

		if (currentPageIndex <= middleIndex) {
			return range(0, Math.min(numberOfButtonsToShow, numberOfPages));
		}

		return range(
			Math.max(0, lastPageIndex - numberOfButtonsToShow),
			lastPageIndex,
		);
	}

	_buttonRenderer({ index, pageIndex, numberOfPages, enabled }) {
		if (index === 0 && pageIndex !== 0) {
			return (html) =>
				html.div(
					{
						class: [
							"navigation-button",
							"offset-button",
							{ disabled: !enabled },
						],
						"data-test": "previous-offset-button",
						click: () => this._shiftTo(-1),
					},
					html.i({ class: "fa-solid fa-ellipsis" }),
				);
		}

		if (
			index === numberOfButtonsToShow - 1 &&
			pageIndex < numberOfPages - 1
		) {
			return (html) =>
				html.div(
					{
						class: [
							"navigation-button",
							"offset-button",
							{ disabled: !enabled },
						],
						"data-test": "next-offset-button",
						click: () => this._shiftTo(1),
					},
					html.i({ class: "fa-solid fa-ellipsis" }),
				);
		}

		return this._goToButtonRenderer({ pageIndex, enabled });
	}

	_goToButtonRenderer({ pageIndex, enabled }) {
		const selected = this._currentPageIndex === pageIndex;
		return (html) =>
			html.div(
				{
					class: [
						"navigation-button",
						"page-button",
						{
							selected,
							disabled: !enabled,
						},
					],
					"data-test": selected
						? "current-page-navigation"
						: "navigation-button",
					click: () => this._triggerOnClick({ pageIndex }),
				},
				pageIndex + 1,
			);
	}

	_triggerOnClick(...args) {
		this._onNavigationClick(...args);
	}

	_shiftTo(index) {
		this._navigationButtonOffset += index;
		this.update();
	}

	static makeForListSource({ listSource, cssClass }) {
		let totalNumberOfItems = null;

		const paginationButtonList = new this({
			cssClass,
			getPageIndex: () => listSource.getPageIndex(),
			getPageSize: () => listSource.getPageSize(),
			getTotalNumberOfItems: () => totalNumberOfItems,
			onNavigationClick: ({ pageIndex }) =>
				listSource.goTo({ pageIndex }),
		});

		listSource.registerOnDataFetch(({ loading, unpaginatedSize }) => {
			if (loading) {
				totalNumberOfItems = null;
			} else {
				totalNumberOfItems = unpaginatedSize;
			}

			paginationButtonList.update();
		});

		return paginationButtonList;
	}
}
